import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/* Inline SVG icons                                                    */
/* ------------------------------------------------------------------ */

function IconLink() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m9.86-4.122a4.5 4.5 0 0 0-1.242-7.244l4.5-4.5a4.5 4.5 0 0 1 6.364 6.364l-1.757 1.757" />
    </svg>
  )
}

function IconSparkles() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
    </svg>
  )
}

function IconFilm() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-7">
      <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
    </svg>
  )
}

function IconShare() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/* Visual mock components                                              */
/* ------------------------------------------------------------------ */

function MockBrowserBar({ url }: { url: string }) {
  return (
    <div className="flex items-center gap-2 rounded-t-lg border-b border-stone bg-white px-4 py-2.5">
      <div className="flex gap-1.5">
        <div className="size-3 rounded-full bg-stone" />
        <div className="size-3 rounded-full bg-stone" />
        <div className="size-3 rounded-full bg-stone" />
      </div>
      <div className="ml-3 flex-1 rounded-md bg-cream px-3 py-1 text-xs text-warm-grey">{url}</div>
    </div>
  )
}

function MockPasteStep() {
  return (
    <div className="overflow-hidden rounded-lg border border-stone shadow-sm">
      <MockBrowserBar url="app.quiteright.co.uk/listings/new" />
      <div className="bg-white p-6">
        <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">Import listing</p>
        <div className="mt-3 flex gap-2">
          <div className="flex-1 rounded-md border border-stone bg-cream px-4 py-2.5 text-sm text-slate">
            https://rightmove.co.uk/properties/156482937
          </div>
          <div className="rounded-md bg-red px-4 py-2.5 text-sm font-medium text-white">Import</div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <div className="size-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <div className="size-2 rounded-full bg-green-500" />
          </div>
          <p className="text-xs text-slate">Extracting property details...</p>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="aspect-[4/3] rounded-md bg-stone/60" />
          ))}
        </div>
        <div className="mt-3 space-y-1.5">
          <div className="h-4 w-3/4 rounded bg-stone/60" />
          <div className="h-3 w-1/2 rounded bg-stone/40" />
          <div className="flex gap-4 mt-2">
            <div className="h-3 w-16 rounded bg-stone/40" />
            <div className="h-3 w-16 rounded bg-stone/40" />
            <div className="h-3 w-16 rounded bg-stone/40" />
          </div>
        </div>
      </div>
    </div>
  )
}

function MockStyleStep() {
  return (
    <div className="overflow-hidden rounded-lg border border-stone shadow-sm">
      <MockBrowserBar url="app.quiteright.co.uk/listings/42" />
      <div className="bg-white p-6">
        <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">Choose your style</p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {[
            { name: "Classic", colour: "bg-red", active: true },
            { name: "Modern", colour: "bg-charcoal", active: false },
            { name: "Prestige", colour: "bg-gold", active: false },
            { name: "Bold", colour: "bg-slate", active: false },
          ].map((t) => (
            <div
              key={t.name}
              className={`rounded-md border p-3 ${t.active ? "border-red ring-1 ring-red" : "border-stone"}`}
            >
              <div className={`h-16 rounded ${t.colour} ${t.active ? "opacity-100" : "opacity-20"}`} />
              <p className={`mt-2 text-xs font-medium ${t.active ? "text-charcoal" : "text-warm-grey"}`}>{t.name}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-md border border-stone bg-cream p-3">
          <p className="text-xs font-medium text-warm-grey">Auto-generated script</p>
          <p className="mt-1.5 text-sm text-charcoal font-display italic">
            &ldquo;Step inside this stunning five-bedroom home on Clifton&rsquo;s Royal York Crescent...&rdquo;
          </p>
        </div>
      </div>
    </div>
  )
}

function MockShareStep() {
  return (
    <div className="overflow-hidden rounded-lg border border-stone shadow-sm">
      <MockBrowserBar url="app.quiteright.co.uk/listings/42/video" />
      <div className="bg-white p-6">
        <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">Share your video</p>
        <div className="mt-3 space-y-2.5">
          {[
            { label: "Download MP4", detail: "1080 × 1920 · 24s · 12 MB", action: "Download" },
            { label: "Email to vendor", detail: "Send a branded video link", action: "Send" },
            { label: "Copy shareable link", detail: "quiteright.co.uk/v/8rYc3", action: "Copy" },
            { label: "Embed on website", detail: "Paste into your listing page", action: "Copy" },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between rounded-md border border-stone px-3 py-2.5">
              <div>
                <p className="text-sm font-medium text-charcoal">{item.label}</p>
                <p className="text-[11px] text-warm-grey">{item.detail}</p>
              </div>
              <span className="rounded-md bg-red px-2.5 py-1 text-[11px] font-medium text-white">{item.action}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2 rounded-md bg-cream px-3 py-2">
          <svg viewBox="0 0 20 20" fill="#22c55e" className="size-4 shrink-0">
            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
          </svg>
          <p className="text-xs text-charcoal font-medium">Rendered in 38 seconds · Full HD · Your branding</p>
        </div>
      </div>
    </div>
  )
}

function MockPublishStep() {
  return (
    <div className="overflow-hidden rounded-lg border border-stone shadow-sm">
      <MockBrowserBar url="app.quiteright.co.uk/listings/42/renders/7" />
      <div className="bg-white p-6">
        <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">Publish to</p>
        <div className="mt-3 space-y-2.5">
          {[
            { platform: "Instagram Reels", colour: "bg-gradient-to-r from-purple-500 to-pink-500", posted: true },
            { platform: "TikTok", colour: "bg-charcoal", posted: true },
            { platform: "YouTube Shorts", colour: "bg-red", posted: false },
            { platform: "Facebook", colour: "bg-blue-600", posted: false },
            { platform: "LinkedIn", colour: "bg-blue-700", posted: false },
          ].map((p) => (
            <div key={p.platform} className="flex items-center justify-between rounded-md border border-stone px-3 py-2.5">
              <div className="flex items-center gap-2.5">
                <div className={`size-3 rounded-sm ${p.colour}`} />
                <span className="text-sm text-charcoal">{p.platform}</span>
              </div>
              {p.posted ? (
                <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">Published</span>
              ) : (
                <span className="rounded-md bg-red px-2.5 py-1 text-[11px] font-medium text-white">Post</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Steps data                                                          */
/* ------------------------------------------------------------------ */

const STEPS = [
  {
    number: "01",
    label: "Import",
    title: "Paste a URL. Save yourself an afternoon.",
    outcome: "While other agents are copy-pasting listing details into a brief for a videographer, you paste one URL and every photo, feature, and detail is ready in seconds.",
    description:
      "Drop a Rightmove, Zoopla, or OnTheMarket URL into QuiteRight. We extract the address, price, photos, bedrooms, features - everything. Or enter details yourself. Either way, your listing is video-ready before the kettle boils.",
    icon: IconLink,
    visual: MockPasteStep,
  },
  {
    number: "02",
    label: "Create",
    title: "A video that makes vendors say: 'that's my agent.'",
    outcome: "Show vendors a professionally scripted, voiced, and branded video ad for their property. It's the kind of marketing that wins the instruction - because it shows you take their property seriously.",
    description:
      "A professional script is written from your listing. Choose a template, pick a voiceover, add background music. Review and tweak until it's perfect. Hit render. In under a minute you have a polished, branded video ad.",
    icon: IconSparkles,
    visual: MockStyleStep,
  },
  {
    number: "03",
    label: "Share",
    title: "Put the video in front of the people who matter.",
    outcome: "Send the video to buyers, tenants, landlords, and vendors. Use it in market appraisals, listing presentations, and client reports. A 15-second video says more than a 500-word description.",
    description:
      "Download the MP4. Email a branded link. Embed it on your website. Send it via WhatsApp. Use it in pitch decks and property particulars. Every video is yours to use however, wherever, and as many times as you like.",
    icon: IconFilm,
    visual: MockShareStep,
  },
  {
    number: "04",
    label: "Publish",
    title: "Be the agent everyone sees on their feed.",
    outcome: "Post to five platforms in one click. Reach buyers on Instagram, landlords on LinkedIn, first-time buyers on TikTok - all while your competitors are still uploading a photo carousel.",
    description:
      "Publish to Instagram Reels, TikTok, YouTube Shorts, Facebook, and LinkedIn. Captions are written for each platform - the right hashtags, the right tone, the right length. One click. Every platform. Done.",
    icon: IconShare,
    visual: MockPublishStep,
  },
]

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function HowItWorks({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        {/* Structured Data - HowTo */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to create property video ads with QuiteRight",
          description: meta.description,
          step: STEPS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.title,
            text: s.description,
          })),
        })}</script>
      </Head>

      {/* Hero — outcome-driven, not process-driven */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">How it works</p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Four steps to being the agent everyone remembers<span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            The agent who sends a vendor a polished video ad wins the instruction over
            the one who sends a PDF brochure. Here&rsquo;s how you become that agent
            - in under a minute, with zero video skills.
          </p>
        </div>
      </section>

      {/* Steps — each framed as a business outcome */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="space-y-24 md:space-y-32">
            {STEPS.map((step, i) => {
              const Icon = step.icon
              const Visual = step.visual
              const reversed = i % 2 !== 0

              return (
                <ScrollReveal
                  key={step.number}
                  className={`flex flex-col items-center gap-12 md:flex-row md:gap-16 ${reversed ? "md:flex-row-reverse" : ""}`}
                >
                  {/* Text */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="flex size-10 items-center justify-center rounded-full bg-red/10 text-red">
                        <Icon />
                      </span>
                      <span className="text-xs font-medium uppercase tracking-[0.2em] text-red">Step {step.number}</span>
                    </div>
                    <h2 className="mt-4 font-display text-3xl font-bold text-charcoal md:text-4xl">
                      {step.title}<span className="text-red">.</span>
                    </h2>

                    {/* Business outcome callout */}
                    <div className="mt-5 rounded-lg border-l-4 border-red bg-red/5 px-4 py-3">
                      <p className="text-sm font-medium leading-relaxed text-charcoal">
                        {step.outcome}
                      </p>
                    </div>

                    <p className="mt-4 text-base leading-relaxed text-slate">
                      {step.description}
                    </p>
                    <div className="mt-1 flex items-center gap-2 text-sm text-warm-grey">
                      <span className="font-display text-4xl font-bold text-stone">{step.number}</span>
                    </div>
                  </div>

                  {/* Visual */}
                  <div className="w-full max-w-md flex-1">
                    <Visual />
                  </div>
                </ScrollReveal>
              )
            })}
          </div>
        </div>
      </section>

      {/* Two use cases — business value of each */}
      <section className="border-t border-stone bg-white px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Two ways to win more business</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-charcoal md:text-4xl">
              Win instructions. Win buyers. Or both<span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-slate">
              Every video you create serves two purposes: impress the vendor who&rsquo;s
              choosing an agent, and reach the buyer who&rsquo;s scrolling social media.
              Most agents use both. The smart ones started yesterday.
            </p>
          </div>
          <ScrollReveal stagger className="mt-12 grid gap-6 md:grid-cols-2">
            {/* Win instructions */}
            <div className="rounded-lg border border-stone bg-cream p-8">
              <div className="flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-full bg-red/10 text-red">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0 1 16.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.023 6.023 0 0 1-2.77.853m0 0a5.96 5.96 0 0 1-1 .084m1-.084a5.96 5.96 0 0 0 .981-3.172M10.478 14.25a5.96 5.96 0 0 0 1-.084m-1 .084a5.96 5.96 0 0 1-.981-3.172" />
                  </svg>
                </span>
                <h3 className="font-display text-xl font-bold text-charcoal">Win more instructions</h3>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate">
                Walk into a market appraisal with a video of a similar property already
                prepared. Show the vendor exactly what their marketing will look like.
                The agent with video wins over the one with a PDF - every time.
              </p>
              <ul className="mt-5 space-y-3">
                {[
                  "Show vendors a video in the market appraisal",
                  "Email branded video links to prospective vendors",
                  "Embed videos on your agency website listings",
                  "Include in pitch decks and property particulars",
                  "Send via WhatsApp to your client pipeline",
                  "Download the MP4 for any use you can think of",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-slate">
                    <svg viewBox="0 0 20 20" fill="#C41E2A" className="mt-0.5 size-4 shrink-0">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Reach buyers */}
            <div className="rounded-lg border border-red/20 bg-red/5 p-8">
              <div className="flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-full bg-red/10 text-red">
                  <IconShare />
                </span>
                <h3 className="font-display text-xl font-bold text-charcoal">Reach every buyer</h3>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate">
                Your next buyer isn&rsquo;t refreshing Rightmove - they&rsquo;re scrolling
                Instagram. Be the agent whose listings stop the scroll. One click puts your
                video in front of buyers, tenants, and investors on every platform.
              </p>
              <ul className="mt-5 space-y-3">
                {[
                  "Instagram Reels - reach local buyers and renters",
                  "TikTok - engage first-time buyers and younger audiences",
                  "YouTube Shorts - appear in property search results",
                  "Facebook - target families and local communities",
                  "LinkedIn - attract landlords and investor clients",
                  "Platform-specific captions written automatically",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-slate">
                    <svg viewBox="0 0 20 20" fill="#C41E2A" className="mt-0.5 size-4 shrink-0">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Stats — framed as competitive advantages */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center font-display text-3xl font-bold text-cream">
            Your competitive edge in numbers<span className="text-red">.</span>
          </h2>
          <ScrollReveal stagger className="mt-12 grid grid-cols-2 gap-8 md:grid-cols-4">
            {[
              { stat: "<60s", label: "Listing to finished video" },
              { stat: "5", label: "Platforms in one click" },
              { stat: "73%", label: "Of vendors prefer video agents" },
              { stat: "0", label: "Video skills required" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-display text-4xl font-bold text-red">{s.stat}</p>
                <p className="mt-2 text-sm text-warm-grey">{s.label}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Supported portals — outgoing authority links */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center font-display text-2xl font-bold text-charcoal md:text-3xl">
            Supported property portals<span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-sm leading-relaxed text-slate">
            QuiteRight imports listing data directly from the UK&rsquo;s most popular property platforms.
            Copy any listing URL and your property details, photos, price, and features are extracted
            automatically, with no manual entry required.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { name: "Rightmove", href: "https://www.rightmove.co.uk", description: "The UK's largest property portal with over one million listings for sale and to let." },
              { name: "Zoopla", href: "https://www.zoopla.co.uk", description: "Property search, valuations, and market insights for buyers, sellers, and agents." },
              { name: "OnTheMarket", href: "https://www.onthemarket.com", description: "Agent-owned property portal offering residential sales and lettings across the UK." },
              { name: "Airbnb", href: "https://www.airbnb.co.uk", description: "The world's largest short-term rental platform, ideal for holiday let marketing." },
              { name: "Booking.com", href: "https://www.booking.com", description: "Global accommodation platform popular with UK holiday let owners and property managers." },
            ].map((portal) => (
              <a
                key={portal.name}
                href={portal.href}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-stone p-4 transition-colors hover:border-red/30 hover:bg-cream"
                title={`Visit ${portal.name}`}
              >
                <p className="font-display text-base font-semibold text-charcoal">{portal.name} &rarr;</p>
                <p className="mt-1 text-xs leading-relaxed text-warm-grey">{portal.description}</p>
              </a>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-warm-grey">
            Don&rsquo;t see your portal? You can always enter listing details manually or contact us
            about integrations.
          </p>
        </div>
      </section>

      {/* CTA — urgency and competitive framing */}
      <section className="border-t border-stone px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
            The agent down the road just posted a video<span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
            They used QuiteRight. It took them 60 seconds. Their vendor is thrilled.
            Your move.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              See it in action - book a demo
            </Link>
            <Link
              href="/examples"
              className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
            >
              Watch real property videos
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
